import React, { useState, useEffect, useCallback, useRef } from "react";
import { RequestBookingService } from '../../services/RequestBookingService';
import { CarModel } from '../../models/CarModel';
import { Booking } from '../../models/booking';
import { getAllCarModels } from '../../services/carModelService';
import { AppContext } from '../../services/AppContext';
import EstimatedFare from './EstimatedFare'
import "../../../styles/commonStyle.css";
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { getCarImage, getCarModelImages } from "../../services/imageService"

interface CarModelSelectorProps {
  onPress: (carModel: string) => void
}

const CarModelSelector: React.FC<CarModelSelectorProps> = ({ onPress }) => {
  const [selectedCar, setSelectedCar] = useState(RequestBookingService.getRequestBooking().booking.selectedCarModel);
  const [carModels, setCarModels] = useState<CarModel[]>([])
  const [openModal, setOpenModal] = useState(false);
  const [openFareModal, setOpenFareModal] = useState(false);
  const [selectedCarDetails, setSelectedCarDetails] = useState<CarModel | null>(null);
  const [fareList, setFareList] = useState<{ [key: string]: number }>({});
  const [bookingForFareModal, setBookingForFareModal] = useState<Booking>();

  const initializeDefaultCar = useRef<boolean | null>(false);

  const getCarModels = async () => {
    const carModels = await getAllCarModels();
    const filteredModels = carModels?.filter((model) => model.status === "ACTIVE");

    if (filteredModels) {
      setCarModels(filteredModels);
    }
  };

  const calculateFaresForAllCars = useCallback(async () => {
    const fares: { [key: string]: number } = {};
    if (carModels.length > 0) {
      // console.log(" calling calculateFaresForAllCars")
      for (const car of carModels) {
        RequestBookingService.getRequestBooking().booking.selectedCarModel = car.carModel;
        RequestBookingService.getRequestBooking().booking.payment.baseFare = car.oneWayMumbai;
        if (RequestBookingService.getRequestBooking().booking.returnRide === true) {
          RequestBookingService.getRequestBooking().booking.payment.baseFare = car.mumbaiReturn;
        }

        await RequestBookingService.calculateFare()

        fares[car.carModel] = RequestBookingService.getRequestBooking().booking.payment.totalFare
      }
      setFareList(fares);
      initializeDefaultCar.current = true
    }
  }, [carModels,]);


  const setInitialCarModel = async () => {
    // console.log("AppContext.selectedCar ", AppContext.selectedCar)
    if (!AppContext.selectedCar) {
      RequestBookingService.getRequestBooking().booking.selectedCarModel = "BYD E6"
      AppContext.selectedCar = carModels[0];
      setSelectedCar("BYD E6")
      // await RequestBookingService.calculateFare();
    }
    else {
      setSelectedCar(AppContext.selectedCar.carModel)
    }
  }

  useEffect(() => {
    getCarModels();
  }, []);

  useEffect(() => {
    calculateFaresForAllCars()
  }, [carModels]);

  useEffect(() => {
    if (initializeDefaultCar.current)
      setInitialCarModel();
  }, [initializeDefaultCar.current]);

  const handleCarSelect = (carModel: CarModel) => {
    AppContext.selectedCar = carModel;
    RequestBookingService.getRequestBooking().booking.selectedCarModel = carModel.carModel;
    setSelectedCar(carModel.carModel);
    onPress(carModel.carModel);
  };

  const handleOpenModal = (carModel: CarModel) => {
    setSelectedCarDetails(carModel);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCarDetails(null);
  };

  const handleOpenFareModal = async (carModel: CarModel) => {
    setSelectedCarDetails(carModel);
    const booking = RequestBookingService.getRequestBooking().booking;
    booking.selectedCarModel = carModel.carModel;
    booking.payment.baseFare = booking.returnRide ? carModel.mumbaiReturn : carModel.oneWayMumbai;

    await RequestBookingService.calculateFare();

    setBookingForFareModal({ ...RequestBookingService.getRequestBooking().booking });
    setOpenFareModal(true);
  };

  const handleCloseFareModal = () => {
    setOpenFareModal(false);
    setSelectedCarDetails(null);
  };

  return (
    <>
      <div className="container-fluid px-0">
        <div className="row g-1">
          {carModels?.map((carModel, index) => (
            <div className="col-12" key={index}>
              <div className={`d-flex p-2 rounded ${selectedCar === carModel.carModel ? 'border border-2 pe-1 border-success' : 'border'} shadow-sm`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleCarSelect(carModel)}
              >
                <div className="col-2 d-flex justify-content-center align-items-center">
                  <img
                    src={getCarImage(carModel.carModel)}
                    alt={carModel.carModel}
                    className="ms-2 rounded"
                    style={{
                      width: '120%',
                      maxHeight: '70px',
                      objectFit: 'contain',
                    }}
                    onClick={(e) => {
                      // e.stopPropagation();
                      if (selectedCar === carModel.carModel) {
                        handleOpenModal(carModel);
                      }
                    }}
                  />
                </div>

                <div className="col-6 pt-1">
                  <div onClick={(e) => {
                    // e.stopPropagation();
                    if (selectedCar === carModel.carModel) {
                      handleOpenModal(carModel);
                    }
                  }}
                  >
                    <p className="fw-bold mb-0 text-start ps-3 lh-base small"
                      style={{ color: selectedCar === carModel.carModel ? 'green' : '#000', }}
                    >
                      {carModel.carModel}
                    </p>
                  </div>
                  <p className="mb-0 text-start ps-3 small text-muted lh-base customTextXs" >
                    {carModel.carType}
                  </p>
                  <p className="mb-0 text-start ps-3 small text-muted lh-base customTextXs" >
                    Max Capacity : {carModel.passengerCapacity} + 1 Driver
                  </p>
                </div>

                <div className="col-4 text-center pt-1">
                  <div onClick={(e) => {
                    // e.stopPropagation();
                    if (selectedCar === carModel.carModel) {
                      handleOpenFareModal(carModel);
                    }
                  }}
                  >
                    <div className="d-flex align-items-center justify-content-center fw-bold mb-0 text-center lh-base small"
                      style={{ color: selectedCar === carModel.carModel ? 'green' : '#000',
                        cursor: 'pointer',
                      }}
                    >
                      ₹{fareList[carModel.carModel] !== undefined ? fareList[carModel.carModel] : 'Loading...'}
                      <FormatListBulletedIcon className="ms-1 fs-6"
                        style={{ color: selectedCar === carModel.carModel ? 'green' : '#000', }}
                      />
                    </div>
                  </div>
                  <p className="mb-0 text-start small text-muted lh-base customTextXs">
                    Including Expressway toll, GST & Extra kms
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Car Details Modal */}
      <div className={`modal fade ${openModal ? 'show d-block' : ''}`} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-center w-100 fw-bold">
                {selectedCarDetails?.carModel}
              </h5>
            </div>

            <div className="modal-body">
              <p className="mb-1 d-flex align-items-center small">
                ● Luggage: {selectedCarDetails?.luggageCapacity}
              </p>
              <p className="mb-1 d-flex align-items-center small">
                ● Passenger Capacity: {selectedCarDetails?.passengerCapacity}
              </p>

              <hr className="mb-3" />

              <div className="row g-2">
                {getCarModelImages[selectedCarDetails?.carModel || '']?.map((image, index) => (
                  <div className="col-6 col-md-4" key={index}>
                    <img
                      src={image}
                      alt={`${selectedCarDetails?.carModel} ${index + 1}`}
                      className="img-fluid rounded"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-success" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Fare Estimate Modal */}
      <div className={`modal fade ${openFareModal ? 'show d-block' : ''}`} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title text-center w-100 fw-bold mb-0">
                Estimated Fare
              </h5>
            </div>

            <div className="modal-body px-2">
              {bookingForFareModal && (
                <EstimatedFare booking={bookingForFareModal} />
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-success" onClick={handleCloseFareModal}>
                Close
            </button>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default CarModelSelector;
